// src/pages/Public/Login.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Image,
} from "react-bootstrap";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaPhone } from "react-icons/fa";
import logo from "../../assets/images/cac-logo.jpeg";

const Login = () => {
  const [identifier, setIdentifier] = useState(""); // ✅ Email OU Téléphone
  const [password, setPassword] = useState("");
  const [inputType, setInputType] = useState("email"); // 'email' ou 'phone'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [attemptCount, setAttemptCount] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Masquer la navbar sur la page de login
    const navbar = document.querySelector(".navbar-custom");
    if (navbar) {
      navbar.style.display = "none";
    }

    setAnimated(true);

    // Restaurer la navbar quand on quitte la page
    return () => {
      if (navbar) {
        navbar.style.display = "";
      }
    };
  }, []);

  // ✅ Validation du formulaire
  const validateForm = () => {
    const errors = {};
    const identifierClean = identifier.trim();

    if (!identifierClean) {
      errors.identifier =
        inputType === "email"
          ? "L'adresse email est requise"
          : "Le numéro de téléphone est requis";
    } else if (
      inputType === "email" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifierClean)
    ) {
      errors.identifier = "Format d'email invalide";
    } else if (inputType === "phone") {
      // Nettoyer et valider le téléphone
      const phoneClean = identifierClean
        .replace(/\s+/g, "")
        .replace(/[^0-9+]/g, "");
      if (phoneClean.length < 8) {
        errors.identifier = "Numéro de téléphone invalide (minimum 8 chiffres)";
      }
    }

    if (!password) {
      errors.password = "Le mot de passe est requis";
    } else if (password.length < 6) {
      errors.password = "Minimum 6 caractères";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ Bascule entre email et téléphone
  const toggleInputType = () => {
    setInputType(inputType === "email" ? "phone" : "email");
    setIdentifier("");
    setFieldErrors({});
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    // ✅ Validation avant envoi
    if (!validateForm()) return;

    setLoading(true);

    try {
      // ✅ Envoyer l'identifiant (email ou téléphone)
      const result = await login(identifier, password);

      if (result.success) {
        const role = result.user.role;
        if (role === "superadmin") navigate("/superadmin");
        else if (role === "admin") navigate("/admin");
        else if (role === "operator") navigate("/operator");
        else navigate("/client");
      } else {
        setAttemptCount((prev) => prev + 1);
        setError("Email/Téléphone ou mot de passe incorrect");
      }
    } catch (err) {
      console.error("Erreur connexion:", err);
      if (err.code === "ERR_NETWORK" || !err.response) {
        setError(
          "Impossible de se connecter au serveur. Vérifiez votre connexion.",
        );
      } else if (err.response?.status === 429) {
        setError("Trop de tentatives. Veuillez patienter.");
      } else {
        setError(
          err.response?.data?.error ||
            "Une erreur est survenue. Veuillez réessayer.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  // ✅ Nettoyer le téléphone pour l'affichage
  const formatPhoneDisplay = (value) => {
    // Permet seulement les chiffres et le +
    return value.replace(/[^0-9+]/g, "");
  };

  return (
    <div className="login-page">
      <div className="login-bg"></div>

      {/* Lien retour accueil */}
      <Link to="/" className="back-home-link">
        <span>←</span> Retour à l'accueil
      </Link>

      <Container className="login-container">
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col lg={4} md={6} sm={8} xs={11}>
            <div className={`login-card-wrapper ${animated ? "slide-up" : ""}`}>
              <Card className="login-card border-0 shadow-lg">
                <Card.Body className="p-0">
                  {/* Header avec Logo */}
                  <div className="login-header">
                    <div className="logo-container">
                      {!logoError ? (
                        <Image
                          src={logo}
                          alt="CAC Logo"
                          className="login-logo-img"
                          onError={handleLogoError}
                        />
                      ) : (
                        <div className="logo-fallback">
                          <span>🚚</span>
                        </div>
                      )}
                    </div>
                    <div className="brand-text">
                      <h2 className="brand-name">CAC</h2>
                      <p className="brand-subtitle">Chine Afrique Cargo</p>
                    </div>
                    <h3 className="login-title">Bienvenue</h3>
                    <p className="login-subtitle">
                      Connectez-vous avec votre email ou numéro de téléphone
                    </p>
                  </div>

                  {/* Formulaire */}
                  <div className="login-form">
                    {error && (
                      <Alert
                        variant="danger"
                        className="alert-custom animate-fadeIn"
                      >
                        <span className="alert-icon">⚠️</span>
                        {error}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="form-group">
                        <Form.Label className="fw-semibold small text-muted">
                          {inputType === "email" ? "📧 Email" : "📱 Téléphone"}
                        </Form.Label>
                        <div className="input-wrapper">
                          {inputType === "email" ? (
                            <FaEnvelope className="input-icon" />
                          ) : (
                            <FaPhone className="input-icon" />
                          )}
                          <Form.Control
                            type={inputType === "email" ? "email" : "text"}
                            value={identifier}
                            onChange={(e) => {
                              const value =
                                inputType === "phone"
                                  ? formatPhoneDisplay(e.target.value)
                                  : e.target.value;
                              setIdentifier(value);
                              if (fieldErrors.identifier)
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  identifier: "",
                                }));
                              if (error) setError("");
                            }}
                            placeholder={
                              inputType === "email"
                                ? "votre email"
                                : "votre numéro de téléphone"
                            }
                            className={`form-control-custom ${fieldErrors.identifier ? "is-invalid" : ""}`}
                            autoComplete={
                              inputType === "email" ? "email" : "tel"
                            }
                          />
                        </div>
                        {fieldErrors.identifier && (
                          <div className="field-error">
                            ⚠️ {fieldErrors.identifier}
                          </div>
                        )}
                        <div className="mt-2 text-end">
                          <button
                            type="button"
                            className="toggle-input-btn"
                            onClick={toggleInputType}
                          >
                            {inputType === "email"
                              ? "🔁 Utiliser le téléphone à la place(indicatif inclu)"
                              : "🔁 Utiliser l'email à la place"}
                          </button>
                        </div>
                      </Form.Group>

                      <Form.Group className="form-group">
                        <Form.Label className="fw-semibold small text-muted">
                          🔑 Mot de passe
                        </Form.Label>
                        <div className="input-wrapper">
                          <FaLock className="input-icon" />
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value);
                              if (fieldErrors.password)
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  password: "",
                                }));
                              if (error) setError("");
                            }}
                            placeholder="Entrez votre mot de passe"
                            className={`form-control-custom ${fieldErrors.password ? "is-invalid" : ""}`}
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex={-1}
                            aria-label={
                              showPassword
                                ? "Masquer le mot de passe"
                                : "Afficher le mot de passe"
                            }
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        {fieldErrors.password && (
                          <div className="field-error">
                            ⚠️ {fieldErrors.password}
                          </div>
                        )}
                      </Form.Group>

                      <div className="form-options">
                        <Link to="/forgot-password" className="forgot-link">
                          Mot de passe oublié ?
                        </Link>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="login-btn"
                      >
                        {loading ? (
                          <>
                            <div className="spinner-custom"></div>
                            <span>Connexion en cours...</span>
                          </>
                        ) : (
                          <>
                            <span>🔐</span> Se connecter
                          </>
                        )}
                      </Button>

                      <div className="login-footer">
                        <p className="mb-0">
                          Pas encore de compte ?{" "}
                          <Link to="/register" className="register-link">
                            S'inscrire
                          </Link>
                        </p>
                      </div>
                    </Form>
                  </div>
                </Card.Body>
              </Card>

              {/* Footer */}
              <div className="login-page-footer">
                <p className="copyright">
                  © {new Date().getFullYear()} CAC - Chine Afrique Cargo
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <style>{`
                .login-page {
                    min-height: 100vh;
                    position: relative;
                    overflow: hidden;
                }
                
                .login-bg {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                    z-index: -1;
                }
                
                .login-bg::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle at 30% 40%, rgba(198, 40, 40, 0.15) 0%, transparent 50%);
                    animation: bgPulse 15s ease-in-out infinite;
                    will-change: transform, opacity;
                }
                
                .login-bg::after {
                    content: '';
                    position: absolute;
                    bottom: -50%;
                    right: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle at 70% 60%, rgba(249, 168, 37, 0.1) 0%, transparent 50%);
                    animation: bgPulse 15s ease-in-out infinite reverse;
                    will-change: transform, opacity;
                }
                
                @keyframes bgPulse {
                    0%, 100% { transform: scale(1); opacity: 0.5; }
                    50% { transform: scale(1.1); opacity: 0.8; }
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease forwards;
                }
                
                .back-home-link {
                    position: absolute;
                    top: 24px;
                    left: 24px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: rgba(255, 255, 255, 0.7);
                    text-decoration: none;
                    font-size: 14px;
                    font-weight: 500;
                    padding: 8px 16px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 40px;
                    backdrop-filter: blur(10px);
                    transition: all 0.3s ease;
                    z-index: 10;
                    will-change: transform, background;
                }
                
                .back-home-link:hover {
                    background: rgba(255, 255, 255, 0.2);
                    color: white;
                    transform: translateX(-4px);
                }
                
                .back-home-link span {
                    font-size: 18px;
                    transition: transform 0.3s ease;
                }
                
                .back-home-link:hover span {
                    transform: translateX(-4px);
                }
                
                .login-container {
                    position: relative;
                    z-index: 1;
                }
                
                .login-card-wrapper {
                    opacity: 0;
                    transform: translateY(30px);
                    will-change: transform, opacity;
                }
                
                .login-card-wrapper.slide-up {
                    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .login-card {
                    border-radius: 32px;
                    overflow: hidden;
                    backdrop-filter: blur(10px);
                    background: rgba(255, 255, 255, 0.98);
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }
                
                .login-header {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    padding: 40px 30px 35px;
                    text-align: center;
                    color: white;
                    position: relative;
                    overflow: hidden;
                }
                
                .login-header::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    right: -50%;
                    width: 200px;
                    height: 200px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                }
                
                .login-header::after {
                    content: '';
                    position: absolute;
                    bottom: -30%;
                    left: -20%;
                    width: 150px;
                    height: 150px;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 50%;
                }
                
                .logo-container {
                    display: flex;
                    justify-content: center;
                    margin-bottom: 16px;
                    position: relative;
                    z-index: 1;
                }
                
                .login-logo-img {
                    width: 80px;
                    height: 80px;
                    object-fit: contain;
                    border-radius: 20px;
                    background: white;
                    padding: 8px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    transition: transform 0.3s ease;
                    will-change: transform;
                }
                
                .login-logo-img:hover {
                    transform: scale(1.05);
                }
                
                .logo-fallback {
                    width: 80px;
                    height: 80px;
                    background: white;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 40px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
                    70% { box-shadow: 0 0 0 15px rgba(255, 255, 255, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
                }
                
                .brand-text {
                    position: relative;
                    z-index: 1;
                    margin-bottom: 20px;
                }
                
                .brand-name {
                    font-size: 32px;
                    font-weight: 800;
                    margin: 0;
                    color: white;
                    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
                    letter-spacing: -0.5px;
                }
                
                .brand-subtitle {
                    font-size: 12px;
                    opacity: 0.9;
                    margin: 4px 0 0;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                }
                
                .login-title {
                    font-size: 22px;
                    font-weight: 600;
                    margin-bottom: 6px;
                    position: relative;
                    z-index: 1;
                }
                
                .login-subtitle {
                    font-size: 14px;
                    opacity: 0.85;
                    margin: 0;
                    position: relative;
                    z-index: 1;
                }
                
                .login-form {
                    padding: 36px 32px;
                }
                
                .form-group {
                    margin-bottom: 22px;
                    position: relative;
                }
                
                .input-wrapper {
                    position: relative;
                }
                
                .input-icon {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #C62828;
                    font-size: 16px;
                    z-index: 1;
                    transition: color 0.3s ease;
                }
                
                .form-control-custom {
                    width: 100%;
                    padding: 15px 16px 15px 48px;
                    border: 2px solid #E2E8F0;
                    border-radius: 14px;
                    font-size: 15px;
                    transition: all 0.3s ease;
                    background: white;
                    will-change: border-color, box-shadow;
                }
                
                .form-control-custom::placeholder {
                    color: #94A3B8;
                    opacity: 1;
                }
                
                .form-control-custom:focus {
                    border-color: #C62828;
                    outline: none;
                    box-shadow: 0 0 0 4px rgba(198, 40, 40, 0.1);
                }
                
                .form-control-custom:focus + .input-icon,
                .input-wrapper:focus-within .input-icon {
                    color: #F9A825;
                }
                
                .password-toggle {
                    position: absolute;
                    right: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #94A3B8;
                    cursor: pointer;
                    padding: 6px;
                    font-size: 16px;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    will-change: color, background;
                }
                
                .password-toggle:hover {
                    color: #C62828;
                    background: rgba(198, 40, 40, 0.05);
                }
                
                .password-toggle:focus-visible {
                    outline: 2px solid #C62828;
                    outline-offset: 2px;
                }
                
                .toggle-input-btn {
                    background: none;
                    border: none;
                    color: #C62828;
                    font-size: 13px;
                    padding: 4px 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border-radius: 6px;
                }
                
                .toggle-input-btn:hover {
                    background: rgba(198, 40, 40, 0.05);
                    text-decoration: underline;
                }
                
                .toggle-input-btn:focus-visible {
                    outline: 2px solid #C62828;
                    outline-offset: 2px;
                }
                
                .form-options {
                    text-align: right;
                    margin-bottom: 28px;
                }
                
                .forgot-link {
                    color: #64748B;
                    text-decoration: none;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    padding: 4px 8px;
                    border-radius: 6px;
                }
                
                .forgot-link:hover {
                    color: #C62828;
                    background: rgba(198, 40, 40, 0.05);
                }
                
                .forgot-link:focus-visible {
                    outline: 2px solid #C62828;
                    outline-offset: 2px;
                }
                
                .alert-custom {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: #FEF2F2;
                    border: 1px solid #FECACA;
                    border-radius: 14px;
                    color: #DC2626;
                    font-size: 14px;
                    padding: 14px 18px;
                    margin-bottom: 24px;
                }
                
                .alert-icon {
                    font-size: 18px;
                }
                
                .field-error {
                    color: #DC2626;
                    font-size: 13px;
                    margin-top: 6px;
                    padding-left: 4px;
                }
                
                .form-control-custom.is-invalid {
                    border-color: #DC2626 !important;
                    background-color: #FEF2F2 !important;
                }
                
                .form-control-custom.is-invalid:focus {
                    box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1) !important;
                }
                
                .login-btn {
                    width: 100%;
                    padding: 15px;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    border-radius: 14px;
                    font-weight: 600;
                    font-size: 16px;
                    color: white;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    position: relative;
                    overflow: hidden;
                    will-change: transform, box-shadow;
                }
                
                .login-btn::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    transition: left 0.5s ease;
                }
                
                .login-btn:hover:not(:disabled)::before {
                    left: 100%;
                }
                
                .login-btn:hover:not(:disabled) {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 30px rgba(198, 40, 40, 0.3);
                }
                
                .login-btn:active:not(:disabled) {
                    transform: translateY(-1px);
                }
                
                .login-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .login-btn:focus-visible {
                    outline: 2px solid white;
                    outline-offset: 2px;
                }
                
                .spinner-custom {
                    width: 18px;
                    height: 18px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                
                .login-footer {
                    text-align: center;
                    margin-top: 28px;
                    padding-top: 24px;
                    border-top: 1px solid #E2E8F0;
                }
                
                .login-footer p {
                    color: #64748B;
                    font-size: 14px;
                }
                
                .register-link {
                    color: #C62828;
                    text-decoration: none;
                    font-weight: 700;
                    transition: all 0.3s ease;
                    margin-left: 4px;
                    padding: 2px 6px;
                    border-radius: 6px;
                }
                
                .register-link:hover {
                    color: #F9A825;
                    text-decoration: underline;
                    background: rgba(198, 40, 40, 0.05);
                }
                
                .register-link:focus-visible {
                    outline: 2px solid #C62828;
                    outline-offset: 2px;
                }
                
                .login-page-footer {
                    text-align: center;
                    margin-top: 20px;
                }
                
                .copyright {
                    color: rgba(255, 255, 255, 0.5);
                    font-size: 12px;
                }
                
                @media (max-width: 768px) {
                    .login-header {
                        padding: 30px 20px 25px;
                    }
                    
                    .login-form {
                        padding: 28px 20px;
                    }
                    
                    .login-logo-img,
                    .logo-fallback {
                        width: 70px;
                        height: 70px;
                        font-size: 32px;
                    }
                    
                    .brand-name {
                        font-size: 28px;
                    }
                    
                    .login-title {
                        font-size: 20px;
                    }
                    
                    .form-control-custom {
                        padding: 13px 16px 13px 45px;
                    }
                    
                    .back-home-link {
                        top: 16px;
                        left: 16px;
                        padding: 6px 14px;
                        font-size: 13px;
                    }
                }
                
                @media (max-width: 480px) {
                    .login-header {
                        padding: 25px 16px 20px;
                    }
                    
                    .login-form {
                        padding: 24px 16px;
                    }
                    
                    .login-logo-img,
                    .logo-fallback {
                        width: 60px;
                        height: 60px;
                        font-size: 28px;
                        border-radius: 16px;
                    }
                    
                    .brand-name {
                        font-size: 24px;
                    }
                    
                    .brand-subtitle {
                        font-size: 10px;
                    }
                    
                    .login-title {
                        font-size: 18px;
                    }
                    
                    .login-subtitle {
                        font-size: 13px;
                    }
                    
                    .form-control-custom {
                        padding: 12px 16px 12px 42px;
                        font-size: 14px;
                    }
                    
                    .login-btn {
                        padding: 13px;
                        font-size: 15px;
                    }
                    
                    .forgot-link {
                        font-size: 13px;
                    }
                }
            `}</style>
    </div>
  );
};

export default Login;
